import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaTooth, FaEye, FaEyeSlash } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import Loader from '../components/Loader';

// Define valid routes to prevent open redirect attacks
const VALID_ROUTES = [
  '/',
  '/student/dashboard',
  '/supervisor/dashboard',
  '/admin/dashboard',
  '/superadmin/dashboard',
  '/assistant/dashboard',
  '/dentist/dashboard',
  '/it/dashboard',
  '/forgot-password',
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  // Hardcoded language (set to 'ar' for Arabic)
  const language = 'en'; // Change to 'ar' for Arabic

  // Initialize Google Sign-In
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
            use_fedcm_for_prompt: false,
            ux_mode: 'popup',
            itp_support: true,
            context: 'signin',
            state_cookie_domain: window.location.hostname,
            hosted_domain: '',
            nonce: Math.random().toString(36).substring(2, 15)
          });
        } catch (error) {
          setError('Failed to initialize Google Sign-In. Please refresh the page.');
        }
      }
    };

    // Load Google Identity Services script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeGoogleSignIn();
      };
      script.onerror = () => {
        setError('Failed to load Google Sign-In. Please check your connection.');
      };
      document.head.appendChild(script);
    } else {
      initializeGoogleSignIn();
    }
  }, []);

  

  // Check Firebase Auth loading status
  useEffect(() => {
    if (window.firebaseAuth) {
      console.log('Firebase Auth ready');
    } else {
      console.log('Firebase Auth not ready yet');
    }
  }, []);

  // Render Google Sign-In button when component mounts
  useEffect(() => {
    const renderGoogleButton = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        const googleButton = document.getElementById('google-signin-button');
        if (googleButton && googleButton.children.length === 0) {
          try {
            // Create a custom button instead of using Google's renderButton
            googleButton.innerHTML = `
              <button type="button" id="custom-google-button" class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300">
                <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            `;
            
            // Add click handler to the custom button
            const customButton = document.getElementById('custom-google-button');
            if (customButton) {
              customButton.addEventListener('click', () => {
                window.google.accounts.id.prompt((notification) => {
                  if (notification.isNotDisplayed()) {
                    setError('Google Sign-In is not available. Please try again.');
                  }
                });
              });
            }
          } catch (error) {
            // If rendering fails, show a simple fallback
            googleButton.innerHTML = '<div class="text-center text-gray-500 text-sm">Google Sign-In loading...</div>';
          }
        }
      }
    };

    // Try to render immediately, then retry after a delay
    renderGoogleButton();
    const timer = setTimeout(renderGoogleButton, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Secure navigation handler
  const safeNavigate = useCallback((path) => {
    if (VALID_ROUTES.includes(path)) {
      navigate(path);
    } else {
      navigate('/'); // Fallback to home
    }
  }, [navigate]);

  // Handle Firebase redirect result (Microsoft fallback flow)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!window.firebaseAuth) return;
        const result = await window.firebaseAuth.getRedirectResult();
        if (!mounted || !result?.user) return;

        const user = result.user;
        const idToken = await user.getIdToken();

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/firebase-microsoft-login`, {
          token: idToken,
          email: user.email,
          name: user.displayName,
          photoURL: user.photoURL
        }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000,
          withCredentials: true,
        });

        const { token, refreshToken, user: backendUser } = response.data;
        await login(token, backendUser, refreshToken);
        const redirectPath = getDashboardPath(backendUser.role);
        safeNavigate(redirectPath);
      } catch (err) {
        // Ignore if there was no redirect-based auth event
        const code = err?.code || '';
        if (code && !String(code).includes('no-auth-event')) {
          console.error('Microsoft redirect sign-in error:', err);
        }
      }
    })();
    return () => { mounted = false; };
  }, [login, safeNavigate]);

  // Handle Google Sign-In response
  const handleGoogleResponse = async (response) => {
    setGoogleLoading(true);
    setError('');

    try {
      const result = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google-login`, {
        token: response.credential,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
        withCredentials: true,
      });

      const { token, refreshToken, user } = result.data;
      await login(token, user, refreshToken);
      const redirectPath = getDashboardPath(user.role);
      safeNavigate(redirectPath);
    } catch (err) {
      let errorMessage = 'Google sign-in failed. Please try again.';
      
      if (err.response?.status === 403) {
        errorMessage = 'Access denied. Your Google account is not registered in the system.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please try again.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(
        language === 'ar'
          ? errorMessage
          : errorMessage
      );
      setGoogleLoading(false);
    }
  };

  // Handle Microsoft Sign-In with Firebase Auth
  const handleMicrosoftSignIn = async () => {
    setMicrosoftLoading(true);
    setError('');
    
    try {
      // Check if Firebase Auth is ready
      if (!window.firebaseAuth) {
        setError('Microsoft login is still loading. Try again in a moment.');
        setMicrosoftLoading(false);
        return;
      }

      console.log('Starting Microsoft login with Firebase...');

      // Create Microsoft provider
      const provider = new window.firebase.auth.OAuthProvider('microsoft.com');
      
      // Set custom parameters for account selection
      provider.setCustomParameters({
        prompt: 'select_account',
        tenant: '37a02594-22f7-411c-9ab1-5bb5e8a6aaed'
      });

      // Add scopes
      provider.addScope('openid');
      provider.addScope('profile');
      provider.addScope('email');

      // Sign in with popup (like Google). If blocked, fallback to redirect
      let result;
      try {
        result = await window.firebaseAuth.signInWithPopup(provider);
      } catch (popupErr) {
        if (popupErr?.code === 'auth/popup-blocked' || popupErr?.code === 'auth/popup-closed-by-user') {
          await window.firebaseAuth.signInWithRedirect(provider);
          return; // flow will resume in getRedirectResult useEffect
        }
        throw popupErr;
      }
      
      console.log('Microsoft login result:', result);

      const user = result.user;
      const idToken = await user.getIdToken();

      // Send Firebase token to backend for validation
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/firebase-microsoft-login`, {
        token: idToken,
        email: user.email,
        name: user.displayName,
        photoURL: user.photoURL
      }, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
        withCredentials: true,
      });

      const { token, refreshToken, user: backendUser } = response.data;
      await login(token, backendUser, refreshToken);
      const redirectPath = getDashboardPath(backendUser.role);
      safeNavigate(redirectPath);
      
    } catch (err) {
      console.error('Microsoft login error:', err);
      let errorMessage = 'Microsoft sign-in failed. Please try again.';
      
      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups for this site and try again.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Your Microsoft account is not registered in the system.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Microsoft authentication failed. Please try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Microsoft sign-in error: ${err.message}`;
      }
      
      setError(errorMessage);
      setMicrosoftLoading(false);
    }
  };

  // Handle Google Sign-In button click
  const handleGoogleSignIn = () => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      try {
        const googleButton = document.getElementById('google-signin-button');
        if (googleButton) {
          googleButton.innerHTML = '';
          
          window.google.accounts.id.renderButton(googleButton, {
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            shape: 'rectangular',
            width: '100%',
            prompt_parent_id: 'google-signin-button',
            use_fedcm_for_prompt: false
          });
        } else {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
              setError('Google Sign-In is not available. Please try again.');
            }
          });
        }
      } catch (error) {
        setError('Failed to start Google Sign-In. Please try again.');
      }
    } else {
      setError('Google Sign-In is not available. Please refresh the page and try again.');
    }
  };

  // Reset Google authentication state when error occurs
  const resetGoogleAuth = () => {
    setError('');
    setGoogleLoading(false);
    if (window.google && window.google.accounts) {
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false,
          ux_mode: 'popup',
          itp_support: true,
          context: 'signin',
          state_cookie_domain: window.location.hostname,
          hosted_domain: '',
          nonce: Math.random().toString(36).substring(2, 15)
        });
      } catch (error) {
        setError('Failed to reinitialize Google Sign-In. Please refresh the page.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Rate limiting check
    const now = Date.now();
    const timeDiff = now - lastAttemptTime;
    
    if (loginAttempts >= 5 && timeDiff < 300000) { // 5 attempts in 5 minutes
      setError('Too many login attempts. Please wait 5 minutes before trying again.');
      setLoading(false);
      return;
    }

    // Reset attempts if more than 5 minutes have passed
    if (timeDiff >= 300000) {
      setLoginAttempts(0);
    }

    // Basic input validation
    if (!email || !password) {
      setError('Please fill in all fields.');
      setLoading(false);
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    // Password length validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
        withCredentials: true,
      });

      const { token, refreshToken, user } = response.data;
      // Reset login attempts on successful login
      setLoginAttempts(0);
      setLastAttemptTime(0);
      await login(token, user, refreshToken);
      const redirectPath = getDashboardPath(user.role);
      safeNavigate(redirectPath);
    } catch (err) {
      // Increment login attempts and update last attempt time
      setLoginAttempts(prev => prev + 1);
      setLastAttemptTime(Date.now());
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Invalid email or password.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Account access denied.';
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(
        language === 'ar'
          ? errorMessage
          : errorMessage
      );
      setLoading(false);
    }
  };

  const getDashboardPath = (role) => {
    switch (role) {
      case 'student':
        return '/student/dashboard';
      case 'supervisor':
        return '/supervisor/dashboard';
      case 'admin':
        return '/admin/dashboard';
      case 'superadmin':
        return '/superadmin/dashboard';
      case 'assistant':
        return '/assistant/dashboard';
      case 'dentist':
        return '/dentist/dashboard';
      case 'it':
        return '/it/dashboard';
      default:
        return '/';
    }
  };

  // Animation variants to match Home.jsx
  const container = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (loading || googleLoading) {
    return <Loader />;
  }

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-br from-[rgba(0,119,182,0.1)] to-[rgba(32,178,170,0.1)] p-4 sm:p-6 relative overflow-hidden" style={{ margin: 0, paddingTop: 0 }}>
      {/* Login Card */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-white bg-opacity-95 rounded-2xl shadow-xl w-full max-w-md p-4 sm:p-6 md:p-8 border border-[rgba(0,119,182,0.2)] z-10"
        style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }}
      >
        <div className="flex justify-center items-center w-full py-4">
          <Link to="/">
            <img 
              src="/imgs/odenta-logo2.jpg"
              alt="ODenta Logo"
              className="h-8 sm:h-10 w-auto"
            />
          </Link>
        </div>

        <motion.h2
          variants={item}
          className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0077B6] text-center mb-2"
        >
          {language === 'ar' ? 'مرحبًا بعودتك' : 'Welcome Back'}
        </motion.h2>
        <motion.p
          variants={item}
          className="text-[#333333] text-center mb-6 text-sm sm:text-base"
        >
          {language === 'ar'
            ? 'سجل الدخول للوصول إلى لوحة التحكم الخاصة بك'
            : 'Sign in to access your dashboard'}
        </motion.p>

        {error && (
          <motion.div
            variants={item}
            className="mb-6 p-3 sm:p-4 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200"
          >
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm">{error}</p>
                {loginAttempts > 0 && loginAttempts < 5 && (
                  <p className="text-xs text-red-600 mt-1">
                    {5 - loginAttempts} attempts remaining
                  </p>
                )}
                {error.includes('Google') && (
                  <button
                    onClick={resetGoogleAuth}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                )}
                {error.includes('Microsoft') && (
                  <button
                    onClick={() => {
                      setError('');
                      setMicrosoftLoading(false);
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    Try again
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <motion.div variants={item}>
            <label
              htmlFor="email"
              className={`block text-sm font-medium text-[#333333] mb-1 ${
                language === 'ar' ? 'text-right' : 'text-left'
              }`}
            >
              {language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA] outline-none transition-colors bg-gray-50 text-[#333333] text-sm sm:text-base"
              placeholder={language === 'ar' ? 'you@example.com' : 'you@example.com'}
              aria-label={language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
            />
          </motion.div>

          <motion.div variants={item}>
            <label
              htmlFor="password"
              className={`block text-sm font-medium text-[#333333] mb-1 ${
                language === 'ar' ? 'text-right' : 'text-left'
              }`}
            >
              {language === 'ar' ? 'كلمة المرور' : 'Password'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA] outline-none transition-colors bg-gray-50 text-[#333333] text-sm sm:text-base pr-10"
                placeholder={language === 'ar' ? '••••••••' : '••••••••'}
                aria-label={language === 'ar' ? 'كلمة المرور' : 'Password'}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#333333] hover:text-[#0077B6]"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <FaEyeSlash className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <FaEye className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </div>
          </motion.div>

          <motion.div
            variants={item}
            className={`text-sm ${language === 'ar' ? 'text-right' : 'text-right'}`}
          >
            <Link
              to="/forgot-password"
              onClick={() => safeNavigate('/forgot-password')}
              className="text-[#0077B6] hover:text-[#20B2AA] font-medium transition-colors"
              aria-label={language === 'ar' ? 'هل نسيت كلمة المرور؟' : 'Forgot your password?'}
            >
              {language === 'ar' ? 'هل نسيت كلمة المرور؟' : 'Forgot your password?'}
            </Link>
          </motion.div>

          <motion.button
            variants={item}
            type="submit"
            disabled={loading}
            className={`w-full bg-gradient-to-r ${
              loading
                ? 'from-[#0077B6]/70 to-[#20B2AA]/70 cursor-not-allowed'
                : 'from-[#0077B6] to-[#20B2AA] hover:from-[#0066A0] hover:to-[#1A9E98]'
            } text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:ring-offset-2 text-sm sm:text-base`}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            aria-label={
              loading
                ? language === 'ar'
                  ? 'جارٍ تسجيل الدخول...'
                  : 'Signing In...'
                : language === 'ar'
                ? 'تسجيل الدخول'
                : 'Sign In'
            }
          >
            {loading
              ? language === 'ar'
                ? 'جارٍ تسجيل الدخول...'
                : 'Signing In...'
              : language === 'ar'
              ? 'تسجيل الدخول'
              : 'Sign In'}
          </motion.button>
        </form>

        {/* Divider */}
        <motion.div variants={item} className="mt-4 sm:mt-6 mb-4 sm:mb-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                {language === 'ar' ? 'أو' : 'or'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Google Sign-In Button */}
        <motion.div
          variants={item}
          className="w-full"
        >
          <div
            id="google-signin-button"
            className={`w-full flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-full shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#20B2AA] transition-all duration-300 ${
              (loading || googleLoading) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {/* Google button will be rendered here automatically */}
            {googleLoading && (
              <div className="flex items-center">
                <FcGoogle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" />
                {language === 'ar' ? 'جارٍ تسجيل الدخول...' : 'Signing in...'}
              </div>
            )}
          </div>
        </motion.div>

        {/* Microsoft Sign-In Button */}
        <motion.div variants={item} className="w-full mt-3">
          <button
            type="button"
            onClick={handleMicrosoftSignIn}
            disabled={loading || microsoftLoading}
            className={`w-full flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 rounded-full shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#20B2AA] transition-all duration-300 ${
              (loading || microsoftLoading) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
              <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
            </svg>
            {microsoftLoading ? (language === 'ar' ? 'جارٍ تسجيل الدخول...' : 'Signing in...') : (language === 'ar' ? 'المتابعة باستخدام مايكروسوفت' : 'Continue with Microsoft')}
          </button>
        </motion.div>

        <motion.div variants={item} className="mt-4 sm:mt-6 text-center">
          <Link
            to="/"
            onClick={() => safeNavigate('/')}
            className="text-[#0077B6] hover:text-[#20B2AA] font-medium transition-colors flex items-center justify-center text-sm sm:text-base"
            aria-label={language === 'ar' ? 'العودة إلى الرئيسية' : 'Back to Home'}
          >
            <span className={language === 'ar' ? 'ml-1' : 'mr-1'}>
              {language === 'ar' ? 'العودة إلى الرئيسية' : 'Back to Home'}
            </span>
            <svg
              className={`w-3 h-3 sm:w-4 sm:h-4 ${language === 'ar' ? 'transform rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
